import React, { useEffect, useLayoutEffect, useRef } from 'react';
import type { DashboardModel } from '../../hooks/useDashboard';
import DashboardFleetManifest from './DashboardFleetManifest';
import DashboardMapPanel from './DashboardMapPanel';
import DashboardDetailTray from './DashboardDetailTray';

/** Walk up the DOM from `el` and return the first element that actually scrolls */
function findScrollParent(el: HTMLElement | null): HTMLElement {
  let node = el?.parentElement ?? null;
  while (node && node !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return document.documentElement;
}

const DashboardContent: React.FC<DashboardModel> = (model) => {
  const { dark, isFullscreen, selectedAircraft, setIsFullscreen } = model;
  // true = compact icon-rail mode, false = full panel
  const [fleetCompact, setFleetCompact] = React.useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Lock scroll ancestor when detail tray is open
  useLayoutEffect(() => {
    const shouldLock = !!selectedAircraft || isFullscreen;
    const scrollEl = findScrollParent(rootRef.current);
    if (shouldLock) {
      scrollEl.style.overflowY = 'hidden';
    } else {
      scrollEl.style.overflowY = '';
    }
    return () => { scrollEl.style.overflowY = ''; };
  }, [!!selectedAircraft, isFullscreen]);

  // Auto-compact when aircraft selected; auto-expand when deselected
  useEffect(() => {
    if (selectedAircraft) {
      setFleetCompact(true);
    } else {
      setFleetCompact(false);
    }
  }, [selectedAircraft]);

  const layout = (
    <div className="flex h-full overflow-hidden gap-3 p-4">
      {/* LEFT: Fleet manifest — full panel or compact icon rail */}
      <div
        className={`shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out ${
          fleetCompact ? 'w-[64px]' : 'w-[330px]'
        }`}
      >
        <div className={`h-full ${fleetCompact ? 'w-[64px]' : 'w-[330px]'} transition-[width] duration-300`}>
          <DashboardFleetManifest
            {...model}
            compact={fleetCompact}
            onToggleCompact={() => setFleetCompact(v => !v)}
          />
        </div>
      </div>

      {/* CENTER: Map */}
      <div className="relative flex-1 min-w-0">
        <DashboardMapPanel {...model} />
      </div>

      {/* RIGHT: Aircraft detail panel */}
      {selectedAircraft && (
        <div className="w-[440px] shrink-0 animate-in slide-in-from-right-6 duration-300">
          <DashboardDetailTray {...model} />
        </div>
      )}
    </div>
  );

  // True fullscreen (map + panels, covers entire viewport)
  if (isFullscreen) {
    return (
      <div
        ref={rootRef}
        className={`fixed inset-0 z-[1000] overflow-hidden animate-in fade-in duration-300 ${
          dark ? 'bg-slate-950 text-white' : 'bg-[#f3f7fb] text-slate-900'
        }`}
      >
        <div className="relative h-full w-full">
          {layout}
        </div>
      </div>
    );
  }

  // Default: contained card — parent is overflow-hidden h-full (flex-1), so h-full fills exactly
  return (
    <div
      ref={rootRef}
      className={`h-full overflow-hidden animate-in fade-in duration-500 ${
        dark ? 'bg-slate-950 text-white' : 'bg-[#f6f9fc] text-slate-900'
      }`}
    >
      <div className="relative w-full h-full overflow-hidden">
        {layout}
      </div>
    </div>
  );
};

export default DashboardContent;
